import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { otpFeedAPI } from '../services/api';

// V71 — renkler Şifre Akışı (pages/OtpFeed.js) ile aynı; widget filtresi bilinçli olarak 4 bankada kalır
const BANK_COLORS = {
  THY: '#e11d48', GARANTI: '#16a34a', ISBANK: '#0ea5e9', AKBANK: '#dc2626',
  YKB: '#7c3aed', ZIRAAT: '#059669', VAKIF: '#0284c7', HALKBANK: '#d97706',
  QNB: '#7c3aed', DENIZ: '#0ea5e9', TEB: '#dc2626', ENPARA: '#f59e0b',
};
const ALLOWED_BANKS = ['THY', 'GARANTI', 'ISBANK', 'AKBANK']; // widget: bilinçli 4 banka kısıtı (35ff755)
const MONO = 'var(--font-mono)';

function timeHMS(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function extractAmount(body) {
  if (!body) return null;
  const m = body.match(/(?<!\d)(\d[\d.,]*)\s*(TRY|TL)\b/i);
  return m ? `${m[1]} ${m[2].toUpperCase()}` : null;
}

function extractMerchant(body) {
  if (!body) return null;
  const m = body.match(/\d{4,8}\.?\s+([A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜ0-9.\-&\s]{2,60}?)\s+(?:icin|firmasinda|isleminizin)/i);
  return m ? m[1].trim().replace(/\s+/g, ' ') : null;
}

function buildInfo(it) {
  if (it.parsed_bank === 'THY') return 'OTP';
  const amt = extractAmount(it.body);
  const mer = extractMerchant(it.body);
  return [amt, mer].filter(Boolean).join(' — ') || (it.sender || '');
}

/* Shared AudioContext — user gesture ile başlatılır, sonra reuse edilir */
let _audioCtx = null;
const getAudioCtx = () => {
  if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (_audioCtx.state === 'suspended') _audioCtx.resume();
  return _audioCtx;
};
/* Sayfa ilk tıklamasında AudioContext'i hazırla */
if (typeof window !== 'undefined') {
  const warmup = () => { try { getAudioCtx(); } catch {} window.removeEventListener('click', warmup); window.removeEventListener('keydown', warmup); };
  window.addEventListener('click', warmup, { once: true });
  window.addEventListener('keydown', warmup, { once: true });
}

/* Global ayar cache — component mount'ta yüklenir (modül seviyesinde çağırma — 401 döngüsü yapar) */
let _otpCfg = { sound: 'on', tone: 'ding' };
let _otpCfgLoaded = false;
const loadOtpCfg = () => {
  if (_otpCfgLoaded) return;
  _otpCfgLoaded = true;
  otpFeedAPI.getTone().then(r => { _otpCfg = { sound: r.data.sound || 'on', tone: r.data.tone || 'ding' }; }).catch(() => { _otpCfgLoaded = false; });
};

const playOtpSound = () => {
  try {
    if (_otpCfg.sound === 'off') return;
    const tone = _otpCfg.tone || 'ding';
    const ctx = getAudioCtx();
    const now = ctx.currentTime;
    const play = (freq, type, start, dur, vol = 0.15) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = type; o.frequency.value = freq;
      g.gain.setValueAtTime(vol, now + start);
      g.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(now + start); o.stop(now + start + dur + 0.05);
    };
    if (tone === 'ding') { play(880,'sine',0,0.35,0.18); play(1174.66,'sine',0.12,0.35,0.18); }
    else if (tone === 'chime') { play(523.25,'sine',0,0.3,0.15); play(659.25,'sine',0.1,0.3,0.15); play(783.99,'sine',0.2,0.4,0.18); }
    else if (tone === 'alert') { play(1200,'square',0,0.08,0.12); play(1200,'square',0.12,0.08,0.12); play(1600,'square',0.24,0.12,0.14); }
    else { play(440,'sine',0,0.5,0.12); play(554.37,'sine',0.05,0.5,0.10); play(659.25,'sine',0.1,0.6,0.08); }
  } catch {}
};

const SHAKE_CSS = `
@keyframes otpShake {
  0%, 100% { transform: translateX(0); }
  10%, 50%, 90% { transform: translateX(-3px); }
  30%, 70% { transform: translateX(3px); }
  20%, 60% { transform: translateX(-2px); }
  40%, 80% { transform: translateX(2px); }
}
`;

const OtpMiniFeed = ({ limit = 4 }) => {
  const [items, setItems] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [shaking, setShaking] = useState(false);
  const socketRef = useRef(null);
  const wrapperRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const res = await otpFeedAPI.getWidget({ limit: 30 });
      const filtered = (res.data || []).filter((r) => ALLOWED_BANKS.includes(r.parsed_bank));
      setItems(filtered.slice(0, limit));
    } catch { /* V71 — widget sessiz kalır; boş durum metni gösterilir */ }
  }, [limit]);

  useEffect(() => {
    fetchAll();
    loadOtpCfg();
    const token = localStorage.getItem('mtu_token');
    if (!token) return;
    const SOCKET_URL = (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/api$/, '');
    socketRef.current = io(SOCKET_URL, {
      auth: { token },
      transports: ['polling'],
      upgrade: false,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
    });
    socketRef.current.on('otp:new', (row) => {
      if (!ALLOWED_BANKS.includes(row.parsed_bank)) return;
      setItems((prev) => [row, ...prev].slice(0, limit));
      setShaking(true);
      setTimeout(() => setShaking(false), 600);
    });
    return () => { socketRef.current?.disconnect(); };
  }, [fetchAll, limit]);

  const copy = (id, code) => {
    if (!code) return;
    try { navigator.clipboard.writeText(code); } catch {}
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  if (!items.length) {
    return <div className="empty-text">Henüz şifre yok.</div>;
  }

  const selColor = selected ? (BANK_COLORS[selected.parsed_bank] || 'var(--slate-600)') : 'var(--slate-600)';

  return (
    <>
      <style>{SHAKE_CSS}</style>
      <div
        ref={wrapperRef}
        style={{
          display: 'flex', flexDirection: 'column',
          animation: shaking ? 'otpShake 0.5s ease-in-out' : 'none',
          boxShadow: shaking ? '0 0 12px rgba(99,102,241,0.4)' : 'none',
          transition: 'box-shadow 0.3s ease',
        }}
      >
        {items.map((it) => {
          const c = BANK_COLORS[it.parsed_bank] || 'var(--slate-600)';
          return (
            <div
              key={it.id}
              onClick={() => setSelected(it)}
              style={{
                display: 'grid',
                gridTemplateColumns: '64px 92px 1fr 78px auto',
                alignItems: 'center', gap: 10, padding: '10px 12px',
                borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer',
                borderLeft: `3px solid ${c}`,
              }}
            >
              <span style={{ background: c, color: '#fff', padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700, textAlign: 'center' }}>
                {it.parsed_bank}
              </span>
              <span style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: c, letterSpacing: 1.5, textAlign: 'left' }}>
                {it.parsed_code || '—'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--fg-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {buildInfo(it)}
              </span>
              <span style={{ fontSize: 10, color: 'var(--fg-tertiary)', fontFamily: MONO, textAlign: 'right' }}>
                TSI {timeHMS(it.received_at || it.created_at)}
              </span>
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                {it.checked_card_id ? (
                  <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 9, color: 'var(--success-700)', fontWeight: 600, border: '1px solid var(--success-200)', borderRadius: 4, padding: '2px 6px', background: 'var(--success-50)' }}>
                      ✓ {it.checked_bank ? `${it.checked_bank} *${it.checked_card_no || ''}` : 'Düşüldü'}
                    </span>
                    {it.checked_by_name && <span style={{ fontSize: 8, color: 'var(--fg-tertiary)' }}>{it.checked_by_name.split(' ')[0]}</span>}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* V71 — ortak modal kalıbı: AppLayout ESC'si .modal-overlay'e tıklar, tema uyumlu */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div
            className="modal modal-sm"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520, borderTop: `4px solid ${selColor}` }}
          >
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ background: selColor, color: '#fff', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                  {selected.parsed_bank || 'BİLİNMİYOR'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--fg-secondary)' }}>{selected.sender || '—'}</span>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)} aria-label="Kapat">×</button>
            </div>

            <div className="modal-body">
              <div style={{ fontSize: 11, color: 'var(--fg-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Mesaj İçeriği</div>
              <div style={{
                background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', borderRadius: 8, padding: '12px 14px',
                fontSize: 13, lineHeight: 1.6, color: 'var(--fg-primary)', wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                fontFamily: MONO,
              }}>
                {selected.body}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--fg-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Parse Edilen Şifre</div>
                  <div style={{ fontFamily: MONO, fontSize: 24, fontWeight: 700, color: selColor, letterSpacing: 2 }}>
                    {selected.parsed_code || '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: 'var(--fg-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Alınma Saati</div>
                  <div style={{ fontSize: 13, color: 'var(--fg-secondary)', fontFamily: MONO }}>
                    TSI {timeHMS(selected.received_at || selected.created_at)}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {selected.parsed_code && (
                <button
                  className="btn"
                  onClick={() => copy(selected.id, selected.parsed_code)}
                  style={{ background: selColor, color: '#fff', border: 'none' }}
                >
                  {copiedId === selected.id ? '✓ Kopyalandı' : 'Şifreyi Kopyala'}
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OtpMiniFeed;
